/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import * as XLSX from 'xlsx';

// Sometimes max sheet ref is messed up, that makes it parse all possilble cells
const fixWorksheetReference = (worksheet: XLSX.WorkSheet) => {
  // get all cells ranges
  const cellRanges = Object.keys(worksheet).filter((x) => x.match(/[A-Z]+[0-9]+/g));

  // get the last one cell range in terms of rows
  const maxCellRange = cellRanges[cellRanges.length - 1];
  if (!maxCellRange) {
    return worksheet;
  }

  const matcher = /[a-zA-Z]+|[0-9]+/g;
  const maxRowResult = maxCellRange.match(matcher);
  if (!maxRowResult) {
    return worksheet;
  }

  // getLast in terms of columns
  const maxCols = cellRanges.sort().pop()?.match(matcher);
  if (!maxCols) {
    return worksheet;
  }

  // Max letter AA + max row 9999 = AA9999
  if (maxCols[0] && maxRowResult[1]) {
    // update the reference because before of that, it could be A1:AMJ1048576
    worksheet['!ref'] = `A1:${maxCols[0] + maxRowResult[1]}`;
  }

  return worksheet;
};

export const getSheetDataObject = (ws: XLSX.WorkSheet) => XLSX.utils.sheet_to_json(fixWorksheetReference(ws));

export const XlsxFile = (file: File, onComplete: (workbook: XLSX.WorkBook) => unknown) => {
  const reader = new FileReader();
  const rABS = !!reader.readAsBinaryString;
  reader.onload = (e) => {
    /* Parse data */
    const bstr = e.target?.result;
    const wb = XLSX.read(bstr, { type: rABS ? 'binary' : 'array' });
    /* Update state */
    onComplete(wb);
  };
  if (rABS) reader.readAsBinaryString(file);
  else reader.readAsArrayBuffer(file);
};

export const getSheetName = (sheetNames: string[], options: string[]): string => {
  if (sheetNames.length === 1) {
    return sheetNames[0];
  }
  return sheetNames.find((n) => options.some((name) => name.includes(n))) ?? '';
};

export interface WorkBookOptions {
  title?: string;
  subject?: string;
  author?: string;
  createDate?: Date;
  sheetNames?: string[];
}

export const getWorkbook = (sheetsData: Array<(string | number)[][]>, options?: WorkBookOptions): XLSX.WorkBook => {
  const wb = XLSX.utils.book_new();
  wb.Props = {
    Title: options?.title,
    Subject: options?.subject,
    Author: options?.author,
    CreatedDate: options?.createDate,
  };

  wb.SheetNames = options?.sheetNames ?? [];
  (options?.sheetNames ?? []).forEach((name, index) => {
    const ws = XLSX.utils.aoa_to_sheet(sheetsData[index]);
    wb.Sheets[name] = ws;
  });

  return XLSX.write(wb, { bookType: 'xlsx', type: 'binary' });
};

export const convertBinaryToOctetStream = (file: any) => {
  var buf = new ArrayBuffer(file.length); //convert s to arrayBuffer
  var view = new Uint8Array(buf); //create uint8array as viewer
  for (var i = 0; i < file.length; i++) view[i] = file.charCodeAt(i) & 0xff; //convert to octet
  return buf;
};
